'use client';

import type { AiReviewSettingsCard } from '@/lib/ai-review-settings/cards';
import { ArrowRight } from 'lucide-react';
import type { ReadonlyURLSearchParams } from 'next/navigation';

import type { Command } from '../types';

import { BreadcrumbLabel } from '../command-item-renderer';
import { COMMAND_GROUPS } from '../types';

import { AI_REVIEW_SETTINGS_COMMAND_CARDS } from '@/lib/ai-review-settings/cards';
import { AI_REVIEW_TAB_LABELS, AI_REVIEW_TABS } from '@/lib/constants';

const BASE_KEYWORDS = ['ai review', 'review settings', 'settings'];
const AI_REVIEW_SETTINGS_LABEL_PREFIX = 'AI review - ';

/**
 * Places card-level AI review settings below primary navigation commands, while
 * keeping them above the standalone Custom agents command at priority 2300.
 */
const AI_REVIEW_SETTINGS_COMMAND_PRIORITY_BASE = 2400;

type BuildAiReviewSettingsCommandsOptions = {
  cards?: AiReviewSettingsCard[];
  priorityBase?: number;
};

type PushAiReviewSettingsHref = (href: string) => void;

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function getAiReviewSettingsTitle(label: string) {
  if (label.startsWith(AI_REVIEW_SETTINGS_LABEL_PREFIX)) {
    return label.slice(AI_REVIEW_SETTINGS_LABEL_PREFIX.length);
  }

  return label;
}

export function getAiReviewSettingsDisplayLabel(label: string) {
  return <BreadcrumbLabel location="AI review" title={getAiReviewSettingsTitle(label)} />;
}

function getAiReviewSettingsKeywords(card: AiReviewSettingsCard) {
  // Searching for Fix with cubic, which the Auto-fix tab also controls, or for the tab's
  // former name, Fixes, finds it.
  const tabKeywords =
    card.tab === AI_REVIEW_TABS.AUTO_FIX
      ? [AI_REVIEW_TAB_LABELS[card.tab], 'Fix with cubic', 'Fixes']
      : [AI_REVIEW_TAB_LABELS[card.tab]];
  return Array.from(
    new Set([...BASE_KEYWORDS, ...tabKeywords, ...card.keywords].filter(isNonEmptyString)),
  );
}

function getAiReviewSettingsHref(card: AiReviewSettingsCard) {
  return `/ai-review?tab=${card.tab}&card=${card.id}`;
}

export function buildAiReviewSettingsCommands(
  push: PushAiReviewSettingsHref,
  {
    cards = AI_REVIEW_SETTINGS_COMMAND_CARDS,
    priorityBase = AI_REVIEW_SETTINGS_COMMAND_PRIORITY_BASE,
  }: BuildAiReviewSettingsCommandsOptions = {},
): Command[] {
  return cards.map((card, index) => ({
    id: card.id,
    label: card.label,
    displayLabel: getAiReviewSettingsDisplayLabel(card.label),
    icon: <ArrowRight />,
    action: () => {
      push(getAiReviewSettingsHref(card));
    },
    keywords: getAiReviewSettingsKeywords(card),
    description: `Open the ${card.label} settings card in AI review settings.`,
    examples: card.keywords.slice(0, 4),
    safeExecution: 'navigation',
    showOn: (path: string, params?: ReadonlyURLSearchParams) =>
      path !== '/ai-review' || params?.get('card') !== card.id,
    group: COMMAND_GROUPS.AI_REVIEW_SETTINGS.name,
    priority: priorityBase - index,
    searchOnly: true,
  }));
}
